// The Audit Findings sheet of Sculpt's structural audit (sculpt-api PLAN-pf-skills.md §5, layout C) — pure layout,
// no Office.js. One row per finding; when a finding has more than one location, the others follow as sub-rows the
// executor groups under it (collapsible row outline). Every location is an internal `HYPERLINK` to its cell or
// range. A finding the agent fixed keeps its row, its status `Fixed` and what was changed in the Fix column. Fed by
// the same `AuditView` the pane's audit card draws. A later card (a fix marked, a fix undone) changes only the Status
// and Fix cells of the findings whose status moved (`findingsSheetEdits`): whatever else the user wrote stays.
import { colToLetter } from './a1';
import { cellLink, textCell } from './auditSheet';
import type { AuditFindingView, AuditSeverity, AuditView } from '../../types/agent';

export const FINDINGS_SHEET_NAME = 'Audit Findings';
export const FINDINGS_COLUMNS = ['#', 'Severity', 'Status', 'Category', 'Finding', 'Location', 'Reaches', 'Evidence', 'Fix'] as const;
export const FINDINGS_HEADER_ROW = 4;
export const FINDINGS_FIRST_ROW = 5;
export const FINDINGS_HEADER_FILL = '#D9E1F2';
export const FINDINGS_SEVERITY_FILLS: Record<AuditSeverity, string> = { Critical: '#F8CBAD', Warning: '#FFE699', Info: '#DDEBF7' };
/** The status cell of a fixed finding (column C). */
export const FINDINGS_FIXED_FILL = '#C6EFCE';
/** Free-text columns keep a width (character units) and wrap; the others are autofitted. */
export const FINDINGS_WRAP_WIDTHS: Record<string, number> = { Finding: 55, Evidence: 45, Fix: 45 };
/** Column A holds the ids (F1…) but also the title and the subtitle above the table: Excel's autofit measures the
 *  whole column whatever range it is given (office-js#5999), so it would size A to the subtitle. A fixed width. */
export const FINDINGS_ID_COLUMN_WIDTH = 6;

type Cell = string | number | null;

export interface FindingsSheetLayout {
  /** Written through `range.formulas` from A1. */
  grid: Cell[][];
  address: string;
  headerAddress: string;
  /** Header + every row, what the autofilter covers; null without findings. */
  tableAddress: string | null;
  /** What autofit measures: the table from column B (column A has a fixed width). */
  autofitAddress: string;
  /** Severity fills, one per finding row (column B), and the status fill of each fixed one (column C). */
  fills: { address: string; color: string }[];
  /** Sub-row runs (1-based, inclusive) to group under the finding row above them. */
  groups: { from: number; to: number }[];
  widths: { column: string; width: number; wrap: boolean }[];
  freezeRows: number;
  findingRows: number;
}

const statusOf = (f: AuditFindingView) => (f.fixed ? 'Fixed' : 'Open');
const fixOf = (f: AuditFindingView) => textCell([f.fixed ? `Fixed: ${f.fixed}` : f.fix, ...f.notes].filter(Boolean).join(' '));

function findingRow(f: AuditFindingView): Cell[] {
  const first = f.locations[0];
  const reaches = f.output ? `${f.output} (impact ${f.impact})` : f.impact ? `impact ${f.impact}` : 'feeds nothing';
  return [
    textCell(f.id), textCell(f.severity), textCell(statusOf(f)), textCell(f.category),
    textCell(f.title + (f.hidden ? ' (hidden sheet)' : '') + (f.locations.length > 1 ? ` — ${f.locations.length} locations` : '')),
    first ? cellLink(first.sheet, `${first.address}`) : '', textCell(reaches), textCell(f.evidence), fixOf(f),
  ];
}

function locationRow(sheet: string, address: string): Cell[] {
  return ['', '', '', '', textCell(`  ${sheet}`), cellLink(sheet, address), '', '', ''];
}

/** A link's visible text is the address alone; the sheet goes in the Finding column of a sub-row. */
export function layoutFindingsSheet(view: AuditView, workbookName: string): FindingsSheetLayout {
  const cols = FINDINGS_COLUMNS.length;
  const blank = (): Cell[] => Array.from({ length: cols }, () => '');
  const title = blank();
  title[0] = textCell(`Structural audit — ${workbookName}`);
  const subtitle = blank();
  const iteration = view.iterative_calculation === null ? 'not read' : view.iterative_calculation ? 'on' : 'OFF';
  subtitle[0] = textCell(`${view.counts.Critical} Critical, ${view.counts.Warning} Warning, ${view.counts.Info} Info · ` +
    `key outputs: ${view.outputs.join(', ') || 'none found'} · iterative calculation: ${iteration}`);
  const header = FINDINGS_COLUMNS.map((c) => c as string);
  const rows: Cell[][] = [];
  const fills: FindingsSheetLayout['fills'] = [];
  const groups: FindingsSheetLayout['groups'] = [];
  for (const f of view.findings) {
    const row = FINDINGS_FIRST_ROW + rows.length;
    rows.push(findingRow(f));
    fills.push({ address: `B${row}`, color: FINDINGS_SEVERITY_FILLS[f.severity] });
    if (f.fixed) fills.push({ address: `C${row}`, color: FINDINGS_FIXED_FILL });
    if (f.locations.length > 1) {
      for (const loc of f.locations.slice(1)) rows.push(locationRow(loc.sheet, loc.address));
      groups.push({ from: row + 1, to: row + f.locations.length - 1 });
    }
  }
  const grid = [title, subtitle, blank(), header, ...rows];
  const last = grid.length;
  const lastCol = colToLetter(cols);
  return {
    grid,
    address: `A1:${lastCol}${last}`,
    headerAddress: `A${FINDINGS_HEADER_ROW}:${lastCol}${FINDINGS_HEADER_ROW}`,
    tableAddress: rows.length ? `A${FINDINGS_HEADER_ROW}:${lastCol}${last}` : null,
    fills,
    groups,
    autofitAddress: `B${FINDINGS_HEADER_ROW}:${lastCol}${last}`,
    widths: [
      { column: 'A', width: FINDINGS_ID_COLUMN_WIDTH, wrap: false },
      ...FINDINGS_COLUMNS.map((c, i) => ({ column: colToLetter(i + 1), width: FINDINGS_WRAP_WIDTHS[c], wrap: true }))
        .filter((w): w is { column: string; width: number; wrap: boolean } => w.width !== undefined),
    ],
    freezeRows: FINDINGS_HEADER_ROW,
    findingRows: view.findings.length,
  };
}

/** One cell to change in an Audit Findings sheet already in the workbook; `fill` null clears the cell's fill. */
export interface FindingsSheetEdit {
  address: string;
  value: string;
  fill?: string | null;
}

/** How far down the header row is looked for: rows the user inserted above the table move it. */
const HEADER_SEARCH_ROWS = 20;

/** The cells that bring a sheet the user already has in line with a newer card: for each finding whose status in
 *  the card differs from the sheet's (fixed, or open again after an undo), its Status cell (and that cell's fill)
 *  and its Fix cell — nothing else. `values` is the sheet from A1. The columns are found by their header, wherever
 *  the user moved them; a row is the finding's only when its id and the start of its title both match, so a sheet
 *  written from another audit, numbered otherwise, is left alone. A sheet without a Status column (written before
 *  it had one) says fixed through its Fix column only. */
export function findingsSheetEdits(values: readonly (readonly unknown[])[], view: AuditView): { edits: FindingsSheetEdit[]; updated: string[] } {
  const text = (v: unknown) => String(v ?? '').trim();      // a text cell's defusing space included (`textCell`)
  const headerRow = values.slice(0, HEADER_SEARCH_ROWS).findIndex((row) => row.some((v) => text(v) === '#') && row.some((v) => text(v) === 'Finding'));
  if (headerRow < 0) return { edits: [], updated: [] };
  const header = values[headerRow].map(text);
  const col = (name: string) => header.indexOf(name);
  const [idCol, findingCol, statusCol, fixCol] = [col('#'), col('Finding'), col('Status'), col('Fix')];
  if (statusCol < 0 && fixCol < 0) return { edits: [], updated: [] };
  const byId = new Map(view.findings.map((f) => [f.id, f]));
  const at = (r: number, c: number) => `${colToLetter(c + 1)}${r + 1}`;
  const edits: FindingsSheetEdit[] = [];
  const updated: string[] = [];
  for (let r = headerRow + 1; r < values.length; r++) {
    const row = values[r];
    const f = byId.get(text(row[idCol]));
    if (!f || !text(row[findingCol]).startsWith(f.title.trim())) continue;
    const shownFixed = statusCol >= 0 ? text(row[statusCol]) === 'Fixed' : text(row[fixCol]).startsWith('Fixed: ');
    if (shownFixed === !!f.fixed) continue;
    if (statusCol >= 0) edits.push({ address: at(r, statusCol), value: textCell(statusOf(f)), fill: f.fixed ? FINDINGS_FIXED_FILL : null });
    if (fixCol >= 0) edits.push({ address: at(r, fixCol), value: fixOf(f) });
    updated.push(f.id);
  }
  return { edits, updated };
}
